import { Button as AntButton } from 'antd'
import styled, { css } from 'styled-components'
import { CustomButtonProps, CustomIconPosition } from './Button'

export const StyledButton = styled(AntButton)<CustomButtonProps>`
  ${({ type = 'primary', theme }) => {
    switch (type) {
      case 'primary':
        return css`
          background-color: ${theme.colors.primary};
          border-color: ${theme.colors.primary};
          color: white;

          &&:hover:not(:disabled) {
            background-color: ${theme.colors.primaryHover};
            border-color: ${theme.colors.primaryHover};
            box-shadow: none;
          }

          &&:active:not(:disabled) {
            background-color: ${theme.colors.primaryActive};
            border-color: ${theme.colors.primaryActive};
            box-shadow: none;
          }

          &&:focus {
            background-color: ${theme.colors.primary};
            border-color: ${theme.colors.primary};
            box-shadow: none;
          }
        `

      case 'secondary':
        return css`
          background-color: white;
          border-color: #1890ff;
          color: #1890ff;

          &:hover:not(:disabled) {
            background-color: #f0f8ff;
            border-color: #40a9ff;
            color: #40a9ff;
          }

          &:focus {
            border-color: #1890ff;
            box-shadow: 0 0 0 2px rgba(24, 144, 255, 0.2);
          }
        `

      case 'danger':
        return css`
          background-color: #ff4d4f;
          border-color: #ff4d4f;
          color: white;

          &:hover:not(:disabled) {
            background-color: #ff7875;
            border-color: #ff7875;
          }

          &:focus {
            background-color: #ff4d4f;
            border-color: #ff4d4f;
            box-shadow: 0 0 0 2px rgba(255, 77, 79, 0.2);
          }
        `

      case 'danger-outline':
        return css`
          background-color: white;
          border-color: ${theme.colors.button.danger.default};
          color: ${theme.colors.button.danger.default};

          svg {
            fill: ${theme.colors.button.danger.default};
          }

          &:hover:not(:disabled) {
            border-color: ${theme.colors.button.danger.hover};
            color: ${theme.colors.button.danger.hover};
            svg {
              fill: ${theme.colors.button.danger.hover};
            }
          }

          &:active {
            border-color: ${theme.colors.button.danger.active};
            color: ${theme.colors.button.danger.active};
            svg {
              fill: ${theme.colors.button.danger.active};
            }
          }
        `

      case 'warning':
        return css`
          background-color: #faad14;
          border-color: #faad14;
          color: white;

          &:hover:not(:disabled) {
            background-color: #ffc53d;
            border-color: #ffc53d;
          }

          &:focus {
            background-color: #faad14;
            border-color: #faad14;
            box-shadow: 0 0 0 2px rgba(250, 173, 20, 0.2);
          }
        `

      case 'success':
        return css`
          background-color: #52c41a;
          border-color: #52c41a;
          color: white;

          &:hover:not(:disabled) {
            background-color: #73d13d;
            border-color: #73d13d;
          }

          &:focus {
            background-color: #52c41a;
            border-color: #52c41a;
            box-shadow: 0 0 0 2px rgba(82, 196, 26, 0.2);
          }
        `

      case 'ghost':
        return css`
          background-color: transparent;
          border-color: #1890ff;
          color: #1890ff;

          &:hover:not(:disabled) {
            background-color: rgba(24, 144, 255, 0.1);
            border-color: #40a9ff;
            color: #40a9ff;
          }

          &:focus {
            border-color: #1890ff;
            box-shadow: 0 0 0 2px rgba(24, 144, 255, 0.2);
          }
        `

      case 'text':
        return css`
          background-color: transparent;
          border-color: transparent;
          color: #1890ff;
          box-shadow: none;

          &:hover:not(:disabled) {
            background-color: rgba(24, 144, 255, 0.1);
            color: #40a9ff;
          }

          &:focus {
            background-color: rgba(24, 144, 255, 0.1);
            color: #1890ff;
          }
        `

      case 'light':
        return css`
          background-color: #f8f9fa;
          border-color: #f8f9fa;
          color: #212529;

          &:hover:not(:disabled) {
            background-color: #e9ecef;
            border-color: #e9ecef;
            color: #212529;
          }

          &:focus {
            background-color: #f8f9fa;
            border-color: #f8f9fa;
            box-shadow: 0 0 0 2px rgba(248, 249, 250, 0.2);
          }
        `

      case 'dark':
        return css`
          background-color: ${theme.colors.button.dark.default};
          color: white;

          &:hover:not(:disabled) {
            background-color: ${theme.colors.button.dark.hover};
          }

          &:active:not(:disabled) {
            background-color: ${theme.colors.button.dark.active};
          }

          &:focus {
            background-color: ${theme.colors.button.dark.hover};
          }
        `

      case 'link':
        return css`
          background-color: transparent;
          border-color: transparent;
          color: #1890ff;
          text-decoration: underline;
          box-shadow: none;

          &:hover:not(:disabled) {
            background-color: transparent;
            color: #40a9ff;
            text-decoration: underline;
          }

          &:focus {
            background-color: transparent;
            color: #1890ff;
            text-decoration: underline;
          }
        `

      case 'invisible':
        return css`
          background-color: transparent;
          border-color: transparent;
          box-shadow: none;
          color: ${theme.colors.primary};

          &:hover:not(:disabled) {
            background-color: ${theme.colors.grayBackground};
            color: ${theme.colors.primary};
          }

          &:active:not(:disabled) {
            background-color: ${theme.colors.button.invisible.active};
          }

          &&:disabled {
            color: ${theme.colors.text};
            opacity: 0.25;
          }
        `

      case 'transparent':
        return css`
          background-color: transparent;
          border-color: transparent;
          color: ${theme.colors.primary};
          box-shadow: none;
        `

      case 'icon':
        return css`
          background-color: transparent;
          border-color: transparent;
          box-shadow: none;
          border-radius: 6px;
          border: 1px solid ${(props) => props.theme.colors.divider};
        `

      case 'dashed':
        return css`
          background-color: white;
          border-color: #d9d9d9;
          border-style: dashed;
          color: rgba(0, 0, 0, 0.85);

          &:hover:not(:disabled) {
            background-color: #fafafa;
            border-color: #40a9ff;
            color: #40a9ff;
          }

          &:focus {
            border-color: #1890ff;
            box-shadow: 0 0 0 2px rgba(24, 144, 255, 0.2);
          }
        `

      default:
        return css``
    }
  }}

  ${({ size = 'middle' }) => {
    switch (size) {
      case 'small':
        return css`
          height: 32px;
          padding: 4px 12px;
          font-size: 14px;
        `
      case 'middle':
        return css`
          height: 40px;
          padding: 6px 16px;
          font-size: 14px;
          font-weight: 600;
          box-shadow: none;
        `
      case 'large':
        return css`
          height: 48px;
          padding: 8px 20px;
          font-size: 18px;
        `
      case 'smallBtnIcon':
        return css`
          height: 28px;
          width: 28px;
          padding: 0;
        `
      default:
        return css``
    }
  }}

  ${({ $fullWidth }) =>
    $fullWidth &&
    css`
      width: 100%;
    `}

  ${({ $rounded }) =>
    $rounded &&
    css`
      border-radius: 24px;
    `}

  display: inline-flex;
  align-items: center;
  justify-content: center;
  line-height: 20px;
  gap: 8px;
  font-weight: 600;
  transition: all 0.2s ease-in-out;
  border-radius: 6px;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  .ant-btn-icon {
    display: flex;
  }
`

export const IconWrapper = styled.span<{ align: CustomIconPosition }>`
  display: flex;
  align-items: center;
  order: ${({ align }) => (align === 'left' ? 0 : 1)};
`
